import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Field, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import { KeyEvent } from 'types';
import { isEnterPressed } from 'utils';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import PencilIcon from 'icons/Pencil.icon';
import { Button } from 'ncoded-component-library';
import QuestionCollapsible from '../../../QuestionCollapsible';
import { FieldArray } from 'react-final-form-arrays';
import PlusIcon from 'icons/Plus.icon';
import {
  QualificationQuestionAnswer,
  QualificationQuestionType,
  SCORE,
} from 'models/DealRoom';
import TrashIcon from 'icons/Trash.icon';
import get from 'lodash.get';
import Answer from './components/Answer';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { uuid4 } from '@sentry/utils';
import useReordering from '../../../../hooks/useReordering';

import './EditQuestion.styles.scss';
import './EditQuestion.styles.responsive.scss';

const ADD_NEW_ANSWER_INITIAL: QualificationQuestionAnswer = {
  answer: '',
  score: SCORE.MEDIUM,
  autoFocus: true,
};

type EditQuestionProps = {
  className?: string;
  name: string;
  onRemove: () => void;
};

const EditQuestion: React.FC<EditQuestionProps> = (props) => {
  const { className, name, onRemove } = props;

  const { values } = useFormState();

  const value = get(values, name) || {};

  const { title, autoFocus } = value as QualificationQuestionType;

  const [isEditingName, setisEditingName] = useState(autoFocus);

  const { isReordering, setIsReordering, onDragEnd } = useReordering();

  const classes = classNames('edit-question', className);

  const { t } = useTranslation();

  const Title = useMemo(
    () =>
      isEditingName ? (
        <Field
          name={`${name}.title`}
          component={InputField}
          autoFocus
          label={t('question')}
          validate={validations.required()}
          customOnBlur={() => setisEditingName(false)}
          onKeyPress={(e: KeyEvent) => {
            if (isEnterPressed(e)) {
              setisEditingName(false);
            }
          }}
        />
      ) : (
        <>
          <Field
            name={`${name}.title`}
            component="input"
            type="hidden"
            validate={validations.required()}
          />
          <p>{title}</p>
          <Button
            variant="icon"
            icon={PencilIcon}
            onClick={() => setisEditingName(true)}
          />
          <Field
            name={`${name}.required`}
            component={CheckboxField}
            type="checkbox"
            label={t('required')}
          />
        </>
      ),
    [isEditingName, name, t, title],
  );

  return (
    <QuestionCollapsible open disabled className={classes}>
      <div className="title">{Title}</div>

      <div className="answers">
        <FieldArray<QualificationQuestionAnswer> name={`${name}.answers`}>
          {({ fields }) => {
            const answersIds = fields.value.map(({ id }) => id);

            return (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={(e) => onDragEnd(e, fields.value, fields.move)}
              >
                <SortableContext
                  items={answersIds}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((name, index) => {
                    const id = fields.value[index].id;

                    return (
                      <Answer
                        key={name}
                        id={id}
                        name={name}
                        onRemove={() => fields.remove(index)}
                        isReordering={isReordering}
                        setIsReordering={setIsReordering}
                      />
                    );
                  })}

                  <div className="actions">
                    <Button
                      variant="link"
                      icon={PlusIcon}
                      onClick={() =>
                        fields.push({
                          ...ADD_NEW_ANSWER_INITIAL,
                          id: uuid4(),
                        })
                      }
                    >
                      {t('addAnswer')}
                    </Button>

                    <Button
                      variant="link"
                      icon={TrashIcon}
                      className="warning-action-button"
                      onClick={onRemove}
                    >
                      {t('deleteQuestion')}
                    </Button>
                  </div>
                </SortableContext>
              </DndContext>
            );
          }}
        </FieldArray>
      </div>
    </QuestionCollapsible>
  );
};

export default EditQuestion;
